import { useState, useEffect, useRef, useCallback } from 'react';
import { healthAPI } from '../api/client';

const POLL_INTERVAL = 15_000;  // проверяем каждые 15 с когда онлайн
const RETRY_INTERVAL = 5_000;  // при недоступности проверяем чаще
const FAIL_THRESHOLD = 2;      // показываем баннер только после N подряд неудачных попыток

/** Ошибка действительно означает недоступность сервера, а не отмену запроса / 401. */
function isConnectivityFailure(error) {
  if (!error) return false;
  if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return false;
  if (error.response?.status === 401 || error.response?.status === 403) return false;
  if (error.response?.status >= 500) return true;
  if (error.code === 'ERR_NETWORK') return true;
  if (error.code === 'ECONNABORTED') return true;
  if (typeof error.message === 'string' && error.message.toLowerCase().includes('network error')) return true;
  return !error.response;
}

export function useServerStatus() {
  const [online, setOnline] = useState(null);
  const [checking, setChecking] = useState(false);

  const failCountRef = useRef(0);
  const timerRef = useRef(null);
  const generationRef = useRef(0);
  const inFlightRef = useRef(false);
  const onlineRef = useRef(null);

  useEffect(() => {
    onlineRef.current = online;
  }, [online]);

  const check = useCallback(async ({ resetFailures = false } = {}) => {
    if (inFlightRef.current) return;
    if (resetFailures) failCountRef.current = 0;

    inFlightRef.current = true;
    setChecking(true);
    try {
      await healthAPI.check();
      failCountRef.current = 0;
      setOnline(true);
    } catch (error) {
      if (!isConnectivityFailure(error)) return;

      failCountRef.current += 1;
      if (failCountRef.current >= FAIL_THRESHOLD) {
        setOnline(false);
      }
    } finally {
      inFlightRef.current = false;
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    const generation = ++generationRef.current;

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const scheduleNext = () => {
      if (generation !== generationRef.current) return;
      clearTimer();
      const delay = onlineRef.current === false ? RETRY_INTERVAL : POLL_INTERVAL;
      timerRef.current = setTimeout(async () => {
        if (generation !== generationRef.current) return;
        await check();
        if (generation !== generationRef.current) return;
        scheduleNext();
      }, delay);
    };

    const initialDelay = setTimeout(async () => {
      if (generation !== generationRef.current) return;
      await check();
      if (generation !== generationRef.current) return;
      scheduleNext();
    }, 2000);

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      // После фона браузер душит таймеры — сбрасываем счётчик и проверяем сразу
      check({ resetFailures: true });
    };

    const onBrowserOnline = () => {
      check({ resetFailures: true });
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onBrowserOnline);

    return () => {
      generationRef.current += 1;
      clearTimeout(initialDelay);
      clearTimer();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onBrowserOnline);
    };
  }, [check]);

  return {
    offline: online === false,
    checking,
    retry: () => check({ resetFailures: true }),
  };
}
